import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import { useShake, useBlow } from '@/lib/motion';
import { Hand, Wind, Microphone } from '@phosphor-icons/react';

/**
 * Drop-in deck of physical-world interactions for a bubble:
 *  - Shake the phone (DeviceMotion) → /jolt kind=shake
 *  - Blow into the mic (AudioContext) → /jolt kind=blow
 *
 * Both update the bubble's energy/mood and surface a quick reaction bubble.
 * Gracefully degrades on desktop / when permissions denied.
 */
const ShakeBlowDeck = ({ bubble, onUpdate }) => {
  const shake = useShake({ enabled: !!bubble });
  const blow = useBlow();
  const [reaction, setReaction] = useState(null); // {text, kind}

  const jolt = async (kind, intensity) => {
    try {
      const { data } = await api.post(`/bubbles/${bubble.id}/jolt`, {
        kind, intensity: Math.max(0.5, Math.min(3, intensity || 1)),
      });
      if (navigator.vibrate) {
        try { navigator.vibrate(kind === 'shake' ? [40, 30, 60] : [20, 20, 80]); } catch {}
      }
      setReaction({ text: data.reaction, kind });
      setTimeout(() => setReaction(null), 2200);
      onUpdate?.();
    } catch (err) {
      // 429 = rate-limited, just show subtle hint
      setReaction({ text: 'I need a minute…', kind: 'cool' });
      setTimeout(() => setReaction(null), 2000);
    }
  };

  useEffect(() => {
    if (!bubble) return;
    const offS = shake.onShake((i) => jolt('shake', i));
    const offB = blow.onBlow((i) => jolt('blow', i));
    return () => { offS && offS(); offB && offB(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bubble?.id, shake.permission, blow.listening]);

  if (!bubble) return null;
  const shakeReady = shake.supported && shake.permission === 'granted';
  const needsShakePerm = shake.supported && shake.permission === 'unknown';

  return (
    <div className="glass rounded-2xl p-5 relative" data-testid="shake-blow-deck">
      <div className="flex items-center gap-2 mb-3">
        <Hand size={16} weight="duotone" className="text-bx-volt" />
        <h3 className="font-display font-bold text-base tracking-tight">Talk with your body</h3>
      </div>
      <p className="text-xs text-white/55 mb-4">
        Shake the phone or blow on the mic. Your bubble feels it.
      </p>

      <div className="grid grid-cols-2 gap-3">
        {/* Shake */}
        {needsShakePerm ? (
          <button data-testid="shake-permission"
                  onClick={() => shake.request()}
                  className="glass-hi rounded-xl p-4 text-left hover:bg-white/10 transition">
            <Hand size={18} weight="bold" className="text-bx-volt" />
            <div className="font-display font-bold text-sm mt-1">Enable shake</div>
            <div className="text-[10px] text-white/45 mt-0.5">Tap to allow motion</div>
          </button>
        ) : (
          <button data-testid="shake-test"
                  onClick={() => jolt('shake', 1.5)}
                  className={`rounded-xl p-4 text-left transition ${
                    shakeReady ? 'glass-hi hover:bg-white/10' : 'border border-dashed border-white/10 opacity-60'
                  }`}>
            <Hand size={18} weight="bold" className="text-bx-volt" />
            <div className="font-display font-bold text-sm mt-1">
              {shakeReady ? 'Shake your phone' : 'Tap to shake'}
            </div>
            <div className="text-[10px] text-white/45 mt-0.5">
              {shakeReady ? 'Motion sensors live' : 'Or use this button'}
            </div>
          </button>
        )}

        {/* Blow */}
        {blow.listening ? (
          <button data-testid="blow-stop"
                  onClick={blow.stop}
                  className="glass-hi rounded-xl p-4 text-left hover:bg-white/10 transition border border-bx-cyan/40">
            <div className="flex items-center gap-1">
              <Microphone size={18} weight="bold" className="text-bx-cyan animate-pulse" />
              <span className="h-2 w-2 rounded-full bg-bx-cyan animate-pulse" />
            </div>
            <div className="font-display font-bold text-sm mt-1">Listening for a breath</div>
            <div className="text-[10px] text-white/45 mt-0.5">Tap to stop</div>
          </button>
        ) : (
          <button data-testid="blow-start"
                  onClick={blow.start}
                  className="glass-hi rounded-xl p-4 text-left hover:bg-white/10 transition">
            <Wind size={18} weight="bold" className="text-bx-cyan" />
            <div className="font-display font-bold text-sm mt-1">Blow on the mic</div>
            <div className="text-[10px] text-white/45 mt-0.5">Enable microphone</div>
          </button>
        )}
      </div>

      {/* Reaction bubble */}
      <AnimatePresence>
        {reaction && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.92 }}
            className="absolute -top-4 right-5 chip text-bx-volt border-bx-volt/40 shadow-lg"
            data-testid="jolt-reaction"
          >
            "{reaction.text}"
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShakeBlowDeck;
